import { useState } from "react";

import styles from "./Settings.module.css";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { changeEmail } from "../../data/api/account";
import { ApiError } from "../../data/api/client";

type Mode = "editing" | "idle" | "sent";

export type EmailChangeProps = {
  readonly email: string;
};

// The email row in Settings → Profile. Email change is a confirmed intent
// (auth-and-sessions.md §Email change): submitting starts the intent and the
// server emails a confirmation link to the new address; the change only lands
// once that link is clicked. This surfaces the three UI states the spec calls
// for — the current address, the edit form, and the "we sent a link" pending
// state with a Resend.
export const EmailChange = ({ email }: EmailChangeProps) => {
  const [mode, setMode] = useState<Mode>("idle");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState("");

  const submit = (): void => {
    const target = next.trim();
    if (!target || busy) {
      return;
    }
    setBusy(true);
    setError(null);
    void changeEmail(target)
      .then(() => {
        setSentTo(target);
        setNext("");
        setMode("sent");
      })
      .catch((e: unknown) => {
        setError(
          e instanceof ApiError
            ? e.message
            : "Couldn't start the email change.",
        );
      })
      .finally(() => setBusy(false));
  };

  if (mode === "idle") {
    return (
      <div className={styles.emailField}>
        <span className={styles.emailCurrent}>{email}</span>
        <Button
          onClick={() => {
            setError(null);
            setMode("editing");
          }}
          size="sm"
          variant="ghost"
        >
          Change
        </Button>
      </div>
    );
  }

  if (mode === "sent") {
    return (
      <div className={styles.emailField}>
        <span className={styles.emailPending}>
          We sent a confirmation link to <strong>{sentTo}</strong>
          <span> — click it to finish switching your email.</span>
        </span>
        <Button
          onClick={() => {
            setNext(sentTo);
            setMode("editing");
          }}
          size="sm"
          variant="ghost"
        >
          Resend
        </Button>
      </div>
    );
  }

  return (
    <div className={styles.emailCol}>
      <div className={styles.emailField}>
        <Input
          autoFocus
          onChange={(e) => setNext(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              submit();
            }
          }}
          placeholder="you@example.com"
          style={{ flex: 1 }}
          type="email"
          value={next}
        />
        <Button
          disabled={busy || !next.trim()}
          onClick={submit}
          size="sm"
          variant="primary"
        >
          Send confirmation
        </Button>
        <Button
          onClick={() => {
            setNext("");
            setError(null);
            setMode("idle");
          }}
          size="sm"
          variant="ghost"
        >
          Cancel
        </Button>
      </div>
      {error ? <div className={styles.emailError}>{error}</div> : null}
    </div>
  );
};
